import React, { useEffect, useState, useContext, useRef } from "react";
import Moment from "react-moment";
import { Modal, Row, Col, Button } from "react-bootstrap";
import { useIntl } from "react-intl";
import { FormattedMessage, injectIntl } from "react-intl";
import PropTypes from "prop-types";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  TablePagination,
  LinearProgress,
  TextField,
  InputAdornment,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { FlitServiceContext } from "../../services/flitService";
import { formatToCurrencyRate } from "./CurrencyRateUIHelper";

const headRows = [
  {
    id: "targetCurrencyCode",
    align: "left",
    disablePadding: true,
    label: "CURRENCY_CODE",
    sortable: true,
  },
  {
    id: "rate",
    align: "right",
    disablePadding: false,
    label: "CURRENCY_RATE",
    sortable: false,
  },
  {
    id: "rateByBase",
    align: "right",
    disablePadding: false,
    label: "RATE_BASE_CURRENCY",
    sortable: false,
  },
  {
    id: "rateOn",
    align: "right",
    disablePadding: false,
    label: "RATE_ON",
    sortable: true,
  },
];

const StyledTableCell = withStyles((theme) => ({
  body: {
    fontSize: 13,
  },
}))(TableCell);

function CurrencyRateTableHead(props) {
  const intl = useIntl();
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headRows.map((row) => (
          <StyledTableCell
            key={row.id}
            align={row.align}
            padding={row.disablePadding ? "none" : "default"}
            sortDirection={orderBy === row.id ? order : false}
          >
            {row.sortable ? (
              <TableSortLabel
                active={orderBy === row.id}
                direction={order}
                onClick={createSortHandler(row.id)}
              >
                {intl.formatMessage({ id: row.label })}
              </TableSortLabel>
            ) : (
              intl.formatMessage({ id: row.label })
            )}
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

CurrencyRateTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  tableWrapper: {
    overflowX: "auto",
  },
  table: {
    minWidth: 500,
  },
  searchField: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
}));

function CurrencyRateListModal(props) {
  const { intl, show, onClose } = props;
  const { flitSvc } = useContext(FlitServiceContext);

  const classes = useStyles();
  const [currencyRates, setCurrencyRates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [searchText, setSearchText] = useState("");
  const searchTimeoutRef = useRef(null);

  const [filter, setFilter] = useState({
    page: 0,
    rowsPerPage: 10,
    orderBy: "rateOn",
    order: "desc",
    searchText: "",
  });

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setFilter({
        page: 0,
        rowsPerPage: 10,
        orderBy: "rateOn",
        order: "desc",
        searchText: "",
      });
      setSearchText("");
      setCurrencyRates([]);
      setTotalRows(0);
    }
  }, [show]);

  // Fetch data when filter changes (removed 'show' from dependencies to prevent double call)
  useEffect(() => {
    if (show) {
      refreshList();
    }
  }, [filter]); // Only filter dependency, not show

  // Debounce search text input
  useEffect(() => {
    if (!show) return;
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      if (filter.searchText !== searchText) {
        setFilter((prev) => ({
          ...prev,
          searchText: searchText,
          page: 0,
        }));
      }
    }, 500);

    // Cleanup
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchText, show]);

  function refreshList() {
    setIsLoading(true);
    
    // Build filter string for API
    const filters = [];
    if (filter.searchText) {
      filters.push({
        raw: `(contains(tolower(targetCurrencyCode),tolower('${filter.searchText}')))`,
      });
    }

    const filterString = flitSvc.makeFilterString(filters);

    flitSvc
      .searchCurrencyRates(
        filter.rowsPerPage,
        filter.page * filter.rowsPerPage,
        filter.orderBy,
        filter.order,
        filterString
      )
      .then(({ data }) => {
        const rates = data.value || [];
        setCurrencyRates(rates);
        setTotalRows(data["@odata.count"] || 0);
      })
      .catch((error) => {
        console.error("Error fetching currency rates:", error);
        setCurrencyRates([]);
        setTotalRows(0);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function handleRequestSort(event, property) {
    const isDesc = filter.orderBy === property && filter.order === "desc";
    const newOrder = isDesc ? "asc" : "desc";
    
    setFilter((prev) => ({
      ...prev,
      order: newOrder,
      orderBy: property,
      page: 0,
    }));
  }

  function handleChangePage(event, newPage) {
    setFilter((prev) => ({
      ...prev,
      page: newPage,
    }));
  }

  function handleChangeRowsPerPage(event) {
    setFilter((prev) => ({
      ...prev,
      rowsPerPage: +event.target.value,
      page: 0,
    }));
  }

  function handleSearchChange(event) {
    setSearchText(event.target.value);
  }

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({ id: "CURRENCY_RATE" })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={4}>
            <TextField
              className={classes.searchField}
              placeholder={intl.formatMessage({ id: "SEARCH" })}
              variant="outlined"
              size="small"
              value={searchText}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <div className={classes.tableWrapper}>
              {isLoading && <LinearProgress />}
              <Table
                className={classes.table}
                aria-labelledby="tableTitle"
                size="small"
              >
                <CurrencyRateTableHead
                  order={filter.order}
                  orderBy={filter.orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {!isLoading && currencyRates.length === 0 ? (
                    <TableRow>
                      <StyledTableCell colSpan={4} align="center">
                        {intl.formatMessage({ id: "NO_DATA_AVAILABLE" })}
                      </StyledTableCell>
                    </TableRow>
                  ) : (
                    currencyRates.map((row, index) => {
                      return (
                        <TableRow hover tabIndex={-1} key={row.id || index}>
                          <StyledTableCell
                            component="th"
                            scope="row"
                            padding="none"
                          >
                            {row.targetCurrencyCode}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {formatToCurrencyRate(
                              row.rate,
                              row.targetCurrencyCode
                            )}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {formatToCurrencyRate(
                              1 / row.rate,
                              row.baseCurrencyCode
                            )}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.rateOn ? (
                              <Moment format="LLL">{row.rateOn}</Moment>
                            ) : (
                              ""
                            )}
                          </StyledTableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
            {!isLoading && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalRows}
                rowsPerPage={filter.rowsPerPage}
                page={filter.page}
                backIconButtonProps={{
                  "aria-label": intl.formatMessage({ id: "PREVIOUS_PAGE" }),
                }}
                nextIconButtonProps={{
                  "aria-label": intl.formatMessage({ id: "NEXT_PAGE" }),
                }}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            )}
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default injectIntl(CurrencyRateListModal);